import React, { useState, useEffect } from 'react';
import { practiceAPI, Question } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { AcademicCapIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const Practice: React.FC = () => {
  const [practiceSettings, setPracticeSettings] = useState({
    total_questions: 20,
    difficulty: '',
    question_type: ''
  });
  const [currentSession, setCurrentSession] = useState<any>(null);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [lastResult, setLastResult] = useState<any>(null);

  const startPractice = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await practiceAPI.startPractice({
        total_questions: practiceSettings.total_questions,
        difficulty: practiceSettings.difficulty ? parseInt(practiceSettings.difficulty) : undefined,
        question_types: practiceSettings.question_type ? [practiceSettings.question_type] : undefined
      });

      if (response.success && response.data) {
        setCurrentSession(response.data);
        setCurrentQuestion(response.data.questions[0]);
        setCurrentQuestionIndex(0);
        setUserAnswer('');
        setShowResult(false);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async () => {
    if (!currentQuestion || !currentSession) return;

    setLoading(true);
    setError('');

    try {
      const response = await practiceAPI.submitAnswer({
        question_id: currentQuestion.id,
        user_answer: userAnswer
      });

      if (response.success && response.data) {
        setLastResult(response.data);
        setShowResult(true);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const nextQuestion = () => {
    if (!currentSession || !currentSession.questions) return;

    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < currentSession.questions.length) {
      setCurrentQuestionIndex(nextIndex);
      setCurrentQuestion(currentSession.questions[nextIndex]);
      setUserAnswer('');
      setShowResult(false);
    } else {
      // 练习完成
      setCurrentSession(null);
      setCurrentQuestion(null);
      setCurrentQuestionIndex(0);
      setShowResult(false);
    }
  };

  const abandonPractice = async () => {
    if (!currentSession) return;

    if (window.confirm('确定要放弃当前练习吗？')) {
      try {
        await practiceAPI.abandonSession(currentSession.session_id);
        setCurrentSession(null);
        setCurrentQuestion(null);
        setCurrentQuestionIndex(0);
        setShowResult(false);
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const renderQuestionOptions = (question: Question) => {
    switch (question.type) {
      case 'single_choice':
      case 'multiple_choice':
        return question.options?.map((option, index) => (
          <label key={index} className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
            <input
              type={question.type === 'single_choice' ? 'radio' : 'checkbox'}
              name="answer"
              value={index}
              checked={userAnswer.includes(index.toString())}
              onChange={(e) => {
                if (question.type === 'single_choice') {
                  setUserAnswer(e.target.value);
                } else {
                  const values = userAnswer.split(',').filter(v => v !== '');
                  if (e.target.checked) {
                    values.push(e.target.value);
                  } else {
                    const index = values.indexOf(e.target.value);
                    if (index > -1) values.splice(index, 1);
                  }
                  setUserAnswer(values.join(','));
                }
              }}
              className="mr-3"
            />
            <span>{String.fromCharCode(65 + index)}. {option}</span>
          </label>
        ));

      case 'true_false':
        return (
          <div className="space-y-3">
            <label className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
              <input
                type="radio"
                name="answer"
                value="true"
                checked={userAnswer === 'true'}
                onChange={(e) => setUserAnswer(e.target.value)}
                className="mr-3"
              />
              <span>正确</span>
            </label>
            <label className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer">
              <input
                type="radio"
                name="answer"
                value="false"
                checked={userAnswer === 'false'}
                onChange={(e) => setUserAnswer(e.target.value)}
                className="mr-3"
              />
              <span>错误</span>
            </label>
          </div>
        );

      case 'fill_blank':
        return (
          <input
            type="text"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            placeholder="请输入答案"
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        );

      case 'subjective':
        return (
          <textarea
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            placeholder="请输入答案"
            rows={4}
            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        );

      default:
        return <p className="text-gray-500">不支持的题目类型</p>;
    }
  };

  const getDifficultyText = (difficulty: number) => {
    const levels = ['', '简单', '较简单', '中等', '较难', '困难'];
    return levels[difficulty] || '未知';
  };

  if (currentSession && currentQuestion) {
    return (
      <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">练习进行中</h2>
            <div className="text-sm text-gray-600">
              进度: {currentQuestionIndex + 1}/{currentSession.total_questions}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
              {error}
            </div>
          )}

          {!showResult ? (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  第 {currentQuestionIndex + 1} 题
                </h3>
                <p className="text-gray-700 mb-4">{currentQuestion.content}</p>
                <div className="text-sm text-gray-600 mb-4">
                  难度: {getDifficultyText(currentQuestion.difficulty)}
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 mb-3">请选择答案：</h4>
                {renderQuestionOptions(currentQuestion)}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={abandonPractice}
                  className="bg-gray-500 text-white py-2 px-4 rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  放弃练习
                </button>
                <button
                  onClick={submitAnswer}
                  disabled={!userAnswer || loading}
                  className="bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? <LoadingSpinner size="sm" /> : '提交答案'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className={`p-4 rounded-md mb-6 ${lastResult.is_correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center mb-2">
                  {lastResult.is_correct ? (
                    <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                  ) : (
                    <XCircleIcon className="h-6 w-6 text-red-500 mr-2" />
                  )}
                  <span className={`font-semibold ${lastResult.is_correct ? 'text-green-800' : 'text-red-800'}`}>
                    {lastResult.is_correct ? '回答正确！' : '回答错误'}
                  </span>
                </div>
                <p className={`text-sm ${lastResult.is_correct ? 'text-green-700' : 'text-red-700'}`}>
                  正确答案: {lastResult.correct_answer}
                </p>
                {lastResult.explanation && (
                  <p className={`text-sm ${lastResult.is_correct ? 'text-green-600' : 'text-red-600'} mt-2`}>
                    解析: {lastResult.explanation}
                  </p>
                )}
              </div>

              <div className="text-sm text-gray-600 mb-6">
                进度: {lastResult.session_progress.answered}/{lastResult.session_progress.total} 
                (正确率: {lastResult.session_progress.accuracy}%)
              </div>

              <div className="flex justify-center">
                {lastResult.completed ? (
                  <div className="text-center">
                    <p className="text-lg font-semibold text-gray-900 mb-4">练习完成！</p>
                    <button
                      onClick={() => {
                        setCurrentSession(null);
                        setCurrentQuestion(null);
                        setCurrentQuestionIndex(0);
                        setShowResult(false);
                      }}
                      className="bg-green-600 text-white py-2 px-6 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      查看结果
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={nextQuestion}
                    className="bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    下一题
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          <AcademicCapIcon className="h-6 w-6 inline mr-2" />
          在线练习
        </h2>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">题目数量</label>
            <select
              value={practiceSettings.total_questions}
              onChange={(e) => setPracticeSettings({ ...practiceSettings, total_questions: parseInt(e.target.value) })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value={10}>10题</option>
              <option value={20}>20题</option>
              <option value={30}>30题</option>
              <option value={50}>50题</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">难度等级</label>
            <select
              value={practiceSettings.difficulty}
              onChange={(e) => setPracticeSettings({ ...practiceSettings, difficulty: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">全部难度</option>
              <option value="1">简单</option>
              <option value="2">较简单</option>
              <option value="3">中等</option>
              <option value="4">较难</option>
              <option value="5">困难</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">题目类型</label>
            <select
              value={practiceSettings.question_type}
              onChange={(e) => setPracticeSettings({ ...practiceSettings, question_type: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">全部类型</option>
              <option value="single_choice">单选题</option>
              <option value="multiple_choice">多选题</option>
              <option value="true_false">判断题</option>
              <option value="fill_blank">填空题</option>
              <option value="subjective">主观题</option>
            </select>
          </div>
        </div>

        <button
          onClick={startPractice}
          disabled={loading}
          className="w-full bg-primary-600 text-white py-3 px-6 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <LoadingSpinner size="sm" /> : '开始练习'}
        </button>
      </div>
    </div>
  );
};

export default Practice;
